import React, { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import {
  Print as PrintIcon,
  Share as ShareIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useOrganizationDataByOrgId } from "../../../../../../hooks/useOrganizationData";
import { useRecoilValue } from "recoil";
import organizationIdState from "../../../../../../atoms/organizationIdAtom";
import { usePDFGenerator } from "../../../../../../hooks/usePDFGenerator";
import useRecipes from "../../../../../../hooks/useRecipes";
import NewColors from "../../../../../../themes/NewColors";

/**
 * MealPlanFloatingActions component provides floating action buttons for meal plan operations
 *
 * @param {Object} mealPlan - The meal plan object
 * @param {Function} onEditClick - Function to handle edit button click
 * @param {Array} ingredients - List of ingredients for the shopping list
 * @param {Array} recipes - Array of recipe objects for PDF generation
 * @param {Object} weeklySchedule - Weekly schedule data for PDF generation
 */
const MealPlanFloatingActions = ({
  mealPlan,
  onEditClick,
  recipes = [],
  weeklySchedule = {},
}) => {
  const organizationId = useRecoilValue(organizationIdState);
  const { organizationData, loading } = useOrganizationDataByOrgId({
    organizationId,
  });
  const { editMealPlan } = useRecipes({ organizationId });
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const { generateMealPlanCalendarPDF, generateMealPlanFullPDF, isGenerating } =
    usePDFGenerator();

  // If loading organization data, return null to avoid rendering before data is ready
  if (loading) return null;

  // Get branding colors from organization or use defaults
  const primaryColor = organizationData?.brandingColors?.primary || NewColors.active.primary;

  // Handle print meal plan - use PDF generation for better quality
  const handlePrint = () => {
    setPrintMenuAnchor(document.querySelector("[data-print-button]"));
  };

  // Handle calendar PDF generation
  const handleCalendarPDF = async () => {
    try {
      await generateMealPlanCalendarPDF(
        mealPlan,
        recipes,
        weeklySchedule,
        organizationData,
      );
    } catch (error) {
      console.error("Error generating calendar PDF:", error);
      alert("Failed to generate calendar PDF. Please try again.");
    }
    setPrintMenuAnchor(null);
  };

  // Handle full meal plan PDF generation
  const handleFullPDF = async () => {
    try {
      await generateMealPlanFullPDF(
        mealPlan,
        recipes,
        weeklySchedule,
        organizationData,
      );
    } catch (error) {
      console.error("Error generating full PDF:", error);
      alert("Failed to generate full PDF. Please try again.");
    }
    setPrintMenuAnchor(null);
  };

  // Handle share meal plan with privacy check
  const handleShare = async () => {
    if (mealPlan.isPublic) {
      // If already public, open the public URL
      if (mealPlan?.id && organizationId) {
        const shareUrl = `${window.location.origin}/meal-plan/${organizationId}/${mealPlan.id}`;
        window.open(shareUrl, "_blank");

        // Show notification
        const notification = document.createElement("div");
        notification.textContent = "Public meal plan opened in new tab!";
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: ${primaryColor};
          color: white;
          padding: 12px 20px;
          border-radius: 4px;
          z-index: 9999;
          font-family: Arial, sans-serif;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        `;
        document.body.appendChild(notification);

        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 3000);
      } else {
        console.log("Missing meal plan ID or organization ID");
      }
    } else {
      // If not public, ask if they want to make it public
      confirmAlert({
        title: "Make Meal Plan Public",
        message:
          "This meal plan is currently private. Would you like to make it public so it can be shared?",
        buttons: [
          {
            label: "Yes, Make Public",
            onClick: async () => {
              try {
                // Update the meal plan to be public
                await editMealPlan({ ...mealPlan, isPublic: true });

                // Open the public URL after making it public
                if (mealPlan?.id && organizationId) {
                  setTimeout(() => {
                    const shareUrl = `${window.location.origin}/meal-plan/${organizationId}/${mealPlan.id}`;
                    window.open(shareUrl, "_blank");

                    const notification = document.createElement("div");
                    notification.textContent =
                      "Meal plan made public and opened in new tab!";
                    notification.style.cssText = `
                      position: fixed;
                      top: 20px;
                      right: 20px;
                      background: ${primaryColor};
                      color: white;
                      padding: 12px 20px;
                      border-radius: 4px;
                      z-index: 9999;
                      font-family: Arial, sans-serif;
                      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                    `;
                    document.body.appendChild(notification);

                    setTimeout(() => {
                      if (document.body.contains(notification)) {
                        document.body.removeChild(notification);
                      }
                    }, 3000);
                  }, 500); // Small delay to allow the update to propagate
                }
              } catch (error) {
                console.error("Failed to make meal plan public:", error);
                alert("Failed to make meal plan public. Please try again.");
              }
            },
          },
          {
            label: "Cancel",
            onClick: () => {},
          },
        ],
      });
    }
  };

  // Handle download meal plan as PDF - show calendar option
  const handleDownload = async () => {
    try {
      await generateMealPlanCalendarPDF(
        mealPlan,
        recipes,
        weeklySchedule,
        organizationData,
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        zIndex: 10,
      }}
    >
      <Tooltip title="Print Meal Plan">
        <IconButton
          data-print-button
          color="primary"
          size="large"
          sx={{
            bgcolor: "primary.main",
            color: "white",
            "&:hover": { bgcolor: "primary.dark" },
            "@media print": { display: "none" },
          }}
          onClick={handlePrint}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <PrintIcon />
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={() => setPrintMenuAnchor(null)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        PaperProps={{
          sx: { minWidth: 200 },
        }}
      >
        <MenuItem onClick={handleCalendarPDF} disabled={isGenerating}>
          <ListItemIcon>
            {isGenerating ? (
              <CircularProgress size={20} />
            ) : (
              <CalendarIcon fontSize="small" />
            )}
          </ListItemIcon>
          Calendar View PDF
        </MenuItem>
        <MenuItem onClick={handleFullPDF} disabled={isGenerating}>
          <ListItemIcon>
            {isGenerating ? (
              <CircularProgress size={20} />
            ) : (
              <PdfIcon fontSize="small" />
            )}
          </ListItemIcon>
          Full Meal Plan PDF
        </MenuItem>
      </Menu>

      <Tooltip title="Share Meal Plan">
        <IconButton
          color="secondary"
          size="large"
          sx={{
            bgcolor: "secondary.main",
            color: "white",
            "&:hover": { bgcolor: "secondary.dark" },
            "@media print": { display: "none" },
          }}
          onClick={handleShare}
        >
          <ShareIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Download Calendar PDF">
        <IconButton
          color="info"
          size="large"
          sx={{
            bgcolor: "info.main",
            color: "white",
            "&:hover": { bgcolor: "info.dark" },
            "@media print": { display: "none" },
          }}
          onClick={handleDownload}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <DownloadIcon />
          )}
        </IconButton>
      </Tooltip>

      <Tooltip title="Edit Meal Plan">
        <IconButton
          color="success"
          size="large"
          sx={{
            bgcolor: "success.main",
            color: "white",
            "&:hover": { bgcolor: "success.dark" },
            "@media print": { display: "none" },
          }}
          onClick={onEditClick}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default MealPlanFloatingActions;
